/** Production site origin for public RealtorsIn APIs (server-side upstream) */
export const REALTORSIN_SITE_URL = "https://api.realtorsin.com";

/** Same-origin proxy — avoids browser CORS when calling the production API */
export const APP_DOWNLOAD_CLICK_API = "/api/app-download/click";

/** Full upstream URL (used by the API route handler only) */
export const APP_DOWNLOAD_CLICK_UPSTREAM = `${REALTORSIN_SITE_URL}/api/app-download/click`;

export const APP_STORE_URLS = {
  android:
    "https://play.google.com/store/apps/details?id=com.rem.realtorsIn",
  ios: "https://apps.apple.com/pk/app/realtorsin/id6752862402",
} as const;

export type AppDownloadPlatform = keyof typeof APP_STORE_URLS;

export type AppDownloadClickPayload = {
  platform: AppDownloadPlatform;
};

/** Normalize and validate platform for the download-click API. */
export function toAppDownloadClickPayload(
  platform: AppDownloadPlatform
): AppDownloadClickPayload {
  return { platform: platform === "ios" ? "ios" : "android" };
}

/**
 * POST /api/app-download/click — tracks store button clicks.
 * Browser calls the same-origin proxy; the route forwards to realtorsin.com.
 */
export async function trackAppDownloadClick(
  platform: AppDownloadPlatform
): Promise<boolean> {
  const payload = toAppDownloadClickPayload(platform);

  try {
    const res = await fetch(APP_DOWNLOAD_CLICK_UPSTREAM, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch (error) {
    console.error("Failed to track app download click:", error);
    return false;
  }
}

/** Fire-and-forget tracking for store anchor clicks (does not block navigation). */
export function trackAppStoreClick(
  platform: AppDownloadPlatform,
  onSuccess?: () => void
): void {
  void trackAppDownloadClick(platform).then((ok) => {
    if (ok) onSuccess?.();
  });
}

/**
 * @deprecated Use an `<a href={APP_STORE_URLS[platform]} target="_blank">` with `trackAppStoreClick` on click.
 */
export async function openAppStoreAfterTrack(
  platform: AppDownloadPlatform,
  onSuccess?: () => void
): Promise<void> {
  const ok = await trackAppDownloadClick(platform);
  if (!ok) return;

  onSuccess?.();
  window.open(APP_STORE_URLS[platform], "_blank", "noopener,noreferrer");
}
